import { z } from "zod";

/**
 * Shared validation schemas.
 *
 * The SAME schemas run on the terminal and in the Worker. A payload the client
 * built and a payload the server accepts are validated by one definition, so
 * they cannot drift — which matters most for sync envelopes, where a shape
 * mismatch would surface as a rejected sale hours after it was rung.
 */

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const uuidSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "Not a UUID");

/**
 * Money is an integer number of minor units. `.int()` here is load-bearing: it
 * is the boundary that stops a float ever entering the system from a client.
 */
export const moneySchema = z.number().int().safe();

export const isoTimestampSchema = z.string().datetime({ offset: false });
export const businessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const taxRegimeSchema = z.enum(["vat", "non_vat"]);
export const orderTypeSchema = z.string().min(1).max(32);
export const tenderTypeSchema = z.string().min(1).max(32);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const pinLoginSchema = z.object({
  staffId: uuidSchema,
  pin: z.string().min(4).max(8).regex(/^\d+$/),
  terminalId: uuidSchema.nullable().optional(),
});

export const managerAuthorizeSchema = z.object({
  managerStaffId: uuidSchema,
  pin: z.string().min(4).max(8).regex(/^\d+$/),
  action: z.string().min(1).max(64),
  context: z.record(z.string(), z.unknown()).optional(),
});

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const categorySchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(120),
  sortOrder: z.number().int().min(0),
  active: z.boolean(),
  /** null means this is a top-level tab. Optional so a pre-nesting server
      payload still validates rather than blanking the menu on an old build. */
  parentId: uuidSchema.nullable().optional().default(null),
});

export const modifierSchema = z.object({
  id: uuidSchema,
  modifierGroupId: uuidSchema,
  name: z.string().min(1).max(120),
  sortOrder: z.number().int().min(0),
  retiredAt: isoTimestampSchema.nullable(),
  priceAdjustment: moneySchema,
});

export const modifierGroupSchema = z.object({
  id: uuidSchema,
  menuItemId: uuidSchema,
  name: z.string().min(1).max(120),
  minSelections: z.number().int().min(0),
  maxSelections: z.number().int().min(0),
  required: z.boolean(),
  sortOrder: z.number().int().min(0),
});

export const variantSchema = z.object({
  id: uuidSchema,
  menuItemId: uuidSchema,
  name: z.string().min(1).max(120),
  isDefault: z.boolean(),
  sortOrder: z.number().int().min(0),
  retiredAt: isoTimestampSchema.nullable(),
  price: moneySchema,
});

export const menuItemSchema = z.object({
  id: uuidSchema,
  categoryId: uuidSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable(),
  imageKey: z.string().max(200).nullable(),
  sortOrder: z.number().int().min(0),
  statutoryDiscountEligible: z.boolean(),
  available: z.boolean(),
  retiredAt: isoTimestampSchema.nullable(),
});

export const catalogSchema = z.object({
  categories: z.array(categorySchema),
  menuItems: z.array(menuItemSchema),
  variants: z.array(variantSchema),
  modifierGroups: z.array(modifierGroupSchema),
  modifiers: z.array(modifierSchema),
});

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

export const saleLineModifierSchema = z.object({
  id: uuidSchema,
  modifierId: uuidSchema,
  nameSnapshot: z.string().min(1).max(120),
  priceAdjustment: moneySchema,
});

export const saleLineSchema = z.object({
  id: uuidSchema,
  saleId: uuidSchema,
  lineNumber: z.number().int().min(1),
  menuItemId: uuidSchema,
  variantId: uuidSchema.nullable(),
  itemNameSnapshot: z.string().min(1).max(120),
  variantNameSnapshot: z.string().max(120).nullable(),
  quantity: z.number().int().positive(),
  unitPrice: moneySchema,
  modifierTotal: moneySchema,
  lineSubtotal: moneySchema,
  lineDiscount: moneySchema,
  lineTotal: moneySchema,
  statutoryEligible: z.boolean(),
  note: z.string().max(200).nullable(),
  modifiers: z.array(saleLineModifierSchema),
});

export const tenderSchema = z.object({
  id: uuidSchema,
  saleId: uuidSchema,
  type: tenderTypeSchema,
  amount: moneySchema.nonnegative(),
  reference: z.string().max(120).nullable(),
  changeGiven: moneySchema.nonnegative(),
  createdAt: isoTimestampSchema,
});

export const discountSchema = z.object({
  id: uuidSchema,
  saleId: uuidSchema,
  saleLineId: uuidSchema.nullable(),
  type: z.enum(["promotional", "senior_citizen", "pwd"]),
  basis: z.string().max(200).nullable(),
  rateBp: z.number().int().min(0).max(10_000),
  amount: moneySchema.nonnegative(),
  coveredCustomers: z.number().int().positive().nullable(),
  totalCustomers: z.number().int().positive().nullable(),
  cardholderName: z.string().max(120).nullable(),
  cardholderId: z.string().max(60).nullable(),
  authorizedBy: uuidSchema.nullable(),
  /** The module that granted a line discount (specs/006-loyalty research R3). */
  grantedByModule: z.string().min(1).max(40).optional(),
  createdAt: isoTimestampSchema,
});

export const saleSchema = z.object({
  id: uuidSchema,
  documentNumber: z.string().min(1).max(40),
  series: z.string().min(1).max(10),
  sequence: z.number().int().positive(),
  terminalId: uuidSchema,
  shiftId: uuidSchema,
  staffId: uuidSchema,
  orderType: orderTypeSchema,
  customerName: z.string().max(120).nullable(),
  subtotal: moneySchema,
  discountTotal: moneySchema,
  statutoryDiscountTotal: moneySchema,
  taxableAmount: moneySchema,
  taxExemptAmount: moneySchema,
  zeroRatedAmount: moneySchema,
  taxAmount: moneySchema,
  total: moneySchema.nonnegative(),
  taxRegime: taxRegimeSchema,
  taxRateBp: z.number().int().min(0).max(10_000),
  activeModules: z.array(z.string()),
  businessDate: businessDateSchema,
  createdAt: isoTimestampSchema,
  lines: z.array(saleLineSchema),
  tenders: z.array(tenderSchema),
  discounts: z.array(discountSchema),
});

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export const envelopeTypeSchema = z.enum([
  "sale.finalize",
  "sale.void",
  "sale.refund",
  "shift.open",
  "shift.close",
  "cash.movement",
  "drawer.no_sale",
  "drawer.event",
  "wifi_voucher.issue",
  "inventory.movement",
  // Loyalty cards (specs/006-loyalty contracts/loyalty-sync.md). An envelope
  // type missing here fails the WHOLE sync request — the sales beside it
  // included — so a module's types must be listed before it ships.
  "loyalty.earn",
  "loyalty.reverse",
  "kds.bump",
]);

export const envelopeSchema = z.object({
  opId: uuidSchema,
  type: envelopeTypeSchema,
  createdAt: isoTimestampSchema,
  payload: z.unknown(),
});

/**
 * The batch cap is 25 and it is DERIVED, not chosen: each envelope's statements
 * travel in one `db.batch()`, so an envelope costs one D1 round trip (two for a
 * correction, which looks up its sale first) against a house budget of 50 per
 * invocation. The server defers, never rejects, whatever a batch cannot afford.
 * Rejecting 26 is deliberate — silently truncating would lose sales.
 */
export const SYNC_BATCH_LIMIT = 25;

export const syncRequestSchema = z.object({
  terminalId: uuidSchema,
  cursor: z.number().int().min(0),
  envelopes: z.array(envelopeSchema).max(SYNC_BATCH_LIMIT),
});

export const terminalRegisterSchema = z.object({
  name: z.string().min(1).max(120),
  setupToken: z.string().min(8).max(200),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type PinLogin = z.infer<typeof pinLoginSchema>;
export type ManagerAuthorize = z.infer<typeof managerAuthorizeSchema>;
export type Catalog = z.infer<typeof catalogSchema>;
export type SalePayload = z.infer<typeof saleSchema>;
export type Envelope = z.infer<typeof envelopeSchema>;
export type SyncRequest = z.infer<typeof syncRequestSchema>;
